"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.YoutubeOEmbedVideoDetails = exports.YoutubeVideo = void 0;
const tslib_1 = require("tslib");
/* eslint-disable camelcase */
const errors_1 = require("@zap-onboard/errors");
const designed_1 = require("designed");
const axios_1 = (0, tslib_1.__importDefault)(require("axios"));
const class_validator_1 = require("class-validator");
const ResponseBase_1 = require("../ResponseBase");
/**
 * See embedding docs:
 * https://developers.google.com/youtube/player_parameters
 */
class YoutubeVideo {
    constructor(youtubeVideoId) {
        this.youtubeVideoId = youtubeVideoId;
    }
    static fromJSON(data) {
        if (data instanceof YoutubeVideo) {
            return data;
        }
        if (typeof data?.youtubeVideoId !== 'string') {
            throw errors_1.errs.UnexpectedError.create('Youtube video was passed non string value');
        }
        const id = data.youtubeVideoId.trim();
        if (id.length !== 11 || !id.match(/^[a-zA-Z0-9_-]{11}$/)) {
            throw errors_1.errs.UnexpectedError.create('That video id is invalid');
        }
        return new YoutubeVideo(id);
    }
    static parse(input) {
        if (typeof input !== 'string') {
            throw errors_1.errs.UnexpectedError.create('YoutubeVideo was passed non string value');
        }
        if (input.trim().length === 11) {
            return YoutubeVideo.fromJSON({ youtubeVideoId: input });
        }
        try {
            const url = new URL(input.startsWith('http') ? input : `https://${input}`);
            const search = new URLSearchParams(url.search);
            if (search.has('v') || search.has('vi')) {
                return YoutubeVideo.fromJSON({
                    youtubeVideoId: search.get('v') ?? search.get('vi'),
                });
            }
            if (!url.pathname.includes('user')) {
                const matched = url.pathname.match(/[a-zA-Z0-9_-]{11}/g);
                if (!matched) {
                    throw errors_1.errs.EntityValidationError.create('Could not find youtube video from input');
                }
                return YoutubeVideo.fromJSON({
                    youtubeVideoId: matched[matched.length - 1],
                });
            }
            // eslint-disable-next-line no-empty
        }
        catch (e) { }
        const matched = input.match(/[a-zA-Z0-9_-]{11}/g);
        if (!matched) {
            throw errors_1.errs.EntityValidationError.create('Could not find youtube video from input');
        }
        return YoutubeVideo.fromJSON({
            youtubeVideoId: matched[matched.length - 1],
        });
    }
    getDetails() {
        return designed_1.AsyncResult.fromPromise(axios_1.default.get('https://youtube.com/oembed', {
            params: {
                url: this.url,
                format: 'json',
            },
        }))
            .map((r) => r.data)
            .map(({ author_name, author_url, thumbnail_url, title }) => YoutubeOEmbedVideoDetails.create({
            authorName: author_name,
            title,
            authorURL: author_url,
            thumbnailURL: thumbnail_url,
        }))
            .mapFailure((err) => {
            if (err instanceof errors_1.errs.EntityValidationError) {
                return new Error('Embed server response failed');
            }
            const status = err?.response?.status;
            if (status === 404) {
                return new Error('Youtube video not found. Ensure it is public or unlisted.');
            }
            if (status === 403) {
                return new Error('Youtube video is private.');
            }
            if (status === 400) {
                return new Error('Youtube video not found. URL might be incorrect.');
            }
            if (typeof status === 'number') {
                return new Error('Could not load youtube video.');
            }
            return err;
        });
    }
    /**
     * Disable google tracking from the youtube embed player, do not advertise
     * other youtube videos on play completion.
     */
    get embedURL() {
        return `https://www.youtube-nocookie.com/embed/${this.youtubeVideoId}?modestbranding=1&showinfo=0&rel=0&cc_load_policy=1&disablekb=1`;
    }
    get url() {
        return `https://youtu.be/${this.youtubeVideoId}`;
    }
    asJSON() {
        return { youtubeVideoId: this.youtubeVideoId };
    }
    toJSON() {
        return this.asJSON();
    }
}
(0, tslib_1.__decorate)([
    designed_1.Memoize.All(),
    (0, tslib_1.__metadata)("design:type", Function),
    (0, tslib_1.__metadata)("design:paramtypes", []),
    (0, tslib_1.__metadata)("design:returntype", designed_1.AsyncResult)
], YoutubeVideo.prototype, "getDetails", null);
exports.YoutubeVideo = YoutubeVideo;
class YoutubeOEmbedVideoDetails extends ResponseBase_1.ResponseBase {
}
(0, tslib_1.__decorate)([
    designed_1.Entity.Field(),
    (0, class_validator_1.IsString)(),
    (0, tslib_1.__metadata)("design:type", String)
], YoutubeOEmbedVideoDetails.prototype, "authorName", void 0);
(0, tslib_1.__decorate)([
    designed_1.Entity.Field(),
    (0, class_validator_1.IsString)(),
    (0, tslib_1.__metadata)("design:type", String)
], YoutubeOEmbedVideoDetails.prototype, "authorURL", void 0);
(0, tslib_1.__decorate)([
    designed_1.Entity.Field(),
    (0, class_validator_1.IsString)(),
    (0, tslib_1.__metadata)("design:type", String)
], YoutubeOEmbedVideoDetails.prototype, "thumbnailURL", void 0);
(0, tslib_1.__decorate)([
    designed_1.Entity.Field(),
    (0, class_validator_1.IsString)(),
    (0, tslib_1.__metadata)("design:type", String)
], YoutubeOEmbedVideoDetails.prototype, "title", void 0);
exports.YoutubeOEmbedVideoDetails = YoutubeOEmbedVideoDetails;
